import React from 'react';
import type { TFunction } from 'i18next';
import type { SidebarEntry } from '../../../components/layout/layoutTypes';
import {
  LayoutDashboard,
  Users,
  UserCircle,
  UserCog,
  Shield,
  Package,
  ShoppingBag,
  ShoppingCart,
  FolderTree,
  Warehouse,
  AlertTriangle,
  DollarSign,
  Settings,
  LayoutTemplate,
  FileText,
  Image,
  Wallet,
  Mail,
  Send,
  Coins,
  BarChart3,
  ClipboardList,
  ClipboardCheck,
  Megaphone,
  CreditCard,
  Truck,
  Route,
  MessageSquareWarning,
  Sparkles,
  Percent,
  MessageSquareQuote,
  BookOpenText,
  Globe,
  Gift,
  ScrollText,
  UserMinus,
  SlidersHorizontal,
} from 'lucide-react';
import { wsPath } from '../../../utils/siteHosts';

const iconClass = 'h-4 w-4 shrink-0';

export function getBackofficeSidebarItems(t: TFunction): SidebarEntry[] {
  return [
    {
      label: t('backoffice.sidebar_overview'),
      icon: <LayoutDashboard className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      children: [
        {
          label: t('backoffice.sidebar_docs', { defaultValue: 'Documentation' }),
          path: wsPath('/workspace/docs'),
          activePathPrefix: '/workspace/docs',
          activePathExcludePrefixes: ['/workspace/docs/technical'],
          icon: <BookOpenText className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['DOC:READ'],
        },
        {
          label: t('backoffice.sidebar_technical_docs', { defaultValue: 'Technical documentation' }),
          path: wsPath('/workspace/docs/technical'),
          icon: <BookOpenText className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['TECH_DOC:READ'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_group_users', 'Users'),
      icon: <Users className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      requiredAnyOf: ['ROLE:READ', 'EMPLOYEE:READ', 'CUSTOMER:READ', 'SUPPLIER:READ'],
      children: [
        {
          label: t('backoffice.sidebar_customers'),
          path: wsPath('/workspace/customers'),
          icon: <UserCircle className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['CUSTOMER:READ'],
        },
        {
          label: t('backoffice.sidebar_account_deletion_retention', {
            defaultValue: 'Account Deletion',
          }),
          path: wsPath('/workspace/account-deletion-retention'),
          icon: <UserMinus className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
        },
        {
          label: t('backoffice.sidebar_employees'),
          path: wsPath('/workspace/employees'),
          icon: <UserCog className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['EMPLOYEE:READ'],
        },
        {
          label: t('backoffice.sidebar_shipping_agents', { defaultValue: 'Shipping agents' }),
          path: wsPath('/workspace/shipping-agents'),
          icon: <Truck className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['EMPLOYEE:READ'],
        },
        {
          label: t('backoffice.sidebar_roles', 'Roles'),
          path: wsPath('/workspace/roles'),
          icon: <Shield className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['ROLE:READ'],
        },
        {
          label: t('backoffice.sidebar_suppliers', 'Suppliers'),
          path: wsPath('/workspace/suppliers'),
          icon: <UserCog className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['SUPPLIER:READ'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_group_products', 'Products'),
      icon: <Package className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      children: [
        {
          label: t('backoffice.sidebar_categories', 'Categories'),
          path: wsPath('/workspace/categories'),
          icon: <FolderTree className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['CATEGORY:READ'],
        },
        {
          label: t('backoffice.sidebar_products', 'Products'),
          path: wsPath('/workspace/products'),
          icon: <ShoppingBag className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['PRODUCT:READ'],
        },
        {
          label: t('backoffice.sidebar_packages', { defaultValue: 'Packages' }),
          path: wsPath('/workspace/packages'),
          icon: <Gift className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['PRODUCT:READ'],
        },
        {
          label: t('backoffice.sidebar_inventory', 'Inventory'),
          path: wsPath('/workspace/inventory'),
          icon: <Warehouse className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['STOCK_LOT:READ', 'PURCHASE:READ'],
        },
        {
          label: t('backoffice.sidebar_purchases', 'Purchase'),
          path: wsPath('/workspace/purchases'),
          icon: <ShoppingCart className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['PURCHASE:READ'],
        },
        {
          label: t('backoffice.sidebar_purchase_orders', 'Purchase Order'),
          path: wsPath('/workspace/purchase-orders'),
          icon: <ClipboardCheck className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['PURCHASE_ORDER:READ'],
        },
        {
          label: t('backoffice.sidebar_expired_products', 'Expired Products'),
          path: wsPath('/workspace/expired-products'),
          icon: <MessageSquareWarning className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['STOCK_LOT:READ', 'LOSS:CREATE'],
        },
        {
          label: t('backoffice.sidebar_losses', 'Loss'),
          path: wsPath('/workspace/losses'),
          icon: <AlertTriangle className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['LOSS:READ'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_finance', { defaultValue: 'Finance' }),
      icon: <DollarSign className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      requiredAnyOf: [
        'FINANCE:READ',
        'BUDGET_PERIOD:READ',
        'PAYMENT:READ',
        'DELIVERY_COST:UPDATE',
        'PROFIT_REPORT:READ',
        'BANK_RECONCILIATION:UPDATE',
      ],
      children: [
        {
          label: t('backoffice.sidebar_payroll', { defaultValue: 'Payroll' }),
          path: wsPath('/workspace/payroll'),
          icon: <Wallet className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['FINANCE:READ'],
        },
        {
          label: t('backoffice.sidebar_finance_dashboard', { defaultValue: 'Dashboard' }),
          path: wsPath('/workspace/finance'),
          icon: <LayoutDashboard className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['FINANCE:READ'],
        },
        {
          label: t('backoffice.sidebar_budget', { defaultValue: 'Budget' }),
          path: wsPath('/workspace/budget'),
          icon: <Wallet className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['BUDGET_PERIOD:READ'],
        },
        {
          label: t('backoffice.sidebar_payments', { defaultValue: 'Payment' }),
          path: wsPath('/workspace/payments'),
          icon: <CreditCard className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['PAYMENT:READ'],
        },
        {
          label: t('backoffice.sidebar_delivery_costs', { defaultValue: 'Delivery Cost' }),
          path: wsPath('/workspace/finance/delivery-costs'),
          icon: <Truck className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['DELIVERY_COST:UPDATE', 'FINANCE:READ'],
        },
        {
          label: t('backoffice.sidebar_profit_reports', { defaultValue: 'Profit' }),
          path: wsPath('/workspace/finance/profit'),
          icon: <BarChart3 className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['PROFIT_REPORT:READ'],
        },
        {
          label: t('backoffice.sidebar_refunds', { defaultValue: 'Refunds' }),
          path: wsPath('/workspace/finance/refunds'),
          icon: <Wallet className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['FINANCE:READ'],
        },
        {
          label: t('backoffice.sidebar_bank_reconciliation', { defaultValue: 'Bank Reconciliation' }),
          path: wsPath('/workspace/finance/reconciliation'),
          icon: <ClipboardList className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['BANK_RECONCILIATION:UPDATE', 'FINANCE:READ'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_group_security', { defaultValue: 'Security' }),
      icon: <Shield className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      requiredAnyOf: ['ACTIVITY:READ'],
      children: [
        {
          label: t('backoffice.sidebar_activities', { defaultValue: 'Activity log' }),
          path: wsPath('/workspace/activities'),
          icon: <ScrollText className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['ACTIVITY:READ'],
        },
        {
          label: t('backoffice.sidebar_staff_action_audit', { defaultValue: 'Staff action audit' }),
          path: wsPath('/workspace/security/action-audit'),
          icon: <ScrollText className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['ACTIVITY:READ'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_group_reports', 'Stats & reports'),
      icon: <BarChart3 className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      children: [
        {
          label: t('backoffice.sidebar_analytics_dashboard', 'Analytics dashboard'),
          path: wsPath('/workspace/reports/analytics'),
          icon: <BarChart3 className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['ORDER_STATS:READ', 'FINANCE:READ'],
        },
        {
          label: t('backoffice.sidebar_order_statistics', 'Order statistics'),
          path: wsPath('/workspace/order-statistics'),
          icon: <ClipboardList className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['ORDER_STATS:READ', 'ORDER:READ'],
        },
        {
          label: t('backoffice.sidebar_reports', 'Budget & activity reports'),
          path: wsPath('/workspace/reports'),
          icon: <BarChart3 className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['BUDGET_PERIOD:READ', 'BUDGET_REPORT:CREATE'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_orders', { defaultValue: 'Orders' }),
      icon: <ClipboardList className={iconClass} strokeWidth={2} />,
      badgeKey: 'orders_pending_ship',
      requiredAnyOf: ['ORDER:READ', 'PROMOTION:READ', 'DELIVERY_BATCH:READ'],
      children: [
        {
          label: t('backoffice.sidebar_orders', { defaultValue: 'Orders' }),
          path: wsPath('/workspace/orders'),
          icon: <ClipboardList className={iconClass} strokeWidth={2} />,
          badgeKey: 'orders_pending_ship',
          requiredAnyOf: ['ORDER:READ'],
        },
        {
          label: t('backoffice.sidebar_promotions', { defaultValue: 'Promotions' }),
          path: wsPath('/workspace/promotions'),
          icon: <Megaphone className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['PROMOTION:READ'],
        },
        {
          label: t('backoffice.sidebar_delivery_batches', { defaultValue: 'Delivery batches' }),
          path: wsPath('/workspace/delivery-batches'),
          icon: <Truck className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['DELIVERY_BATCH:READ'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_group_marketing', { defaultValue: 'Marketing' }),
      icon: <Megaphone className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      children: [
        {
          label: t('backoffice.sidebar_landing_promotion', { defaultValue: 'Landing promotion' }),
          path: wsPath('/workspace/landing-promotion'),
          icon: <Megaphone className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
        },
        {
          label: t('backoffice.sidebar_recommendations', { defaultValue: 'Recommendations' }),
          path: wsPath('/workspace/recommendations'),
          icon: <Sparkles className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
        },
        {
          label: t('backoffice.sidebar_checkout_marketing', { defaultValue: 'Checkout marketing' }),
          path: wsPath('/workspace/checkout-marketing'),
          icon: <Percent className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_group_feedback', { defaultValue: 'Feedback' }),
      icon: <MessageSquareWarning className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      badgeKey: 'feedback_open',
      requiredAnyOf: ['ORDER:READ', 'UI_SETTING:READ', 'UI_SETTING:UPDATE'],
      children: [
        {
          label: t('backoffice.sidebar_feedback_inbox', { defaultValue: 'Feedback inbox' }),
          path: wsPath('/workspace/feedback-inbox'),
          icon: <MessageSquareWarning className={iconClass} strokeWidth={2} />,
          badgeKey: 'feedback_open',
          requiredAnyOf: ['ORDER:READ'],
        },
        {
          label: t('backoffice.sidebar_testimonials', { defaultValue: 'Testimonials' }),
          path: wsPath('/workspace/testimonials'),
          icon: <MessageSquareQuote className={iconClass} strokeWidth={2} />,
          requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
        },
      ],
    },
    {
      label: t('backoffice.sidebar_group_settings', { defaultValue: 'Settings' }),
      icon: <Settings className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      requiredAnyOf: [
        'UI_SETTING:READ',
        'UI_SETTING:UPDATE',
        'HERO:READ',
        'DEPARTMENT:READ',
        'DEPARTMENT:CREATE',
        'DEPARTMENT:UPDATE',
        'CURRENCY:READ',
        'DELIVERY_PRICING:READ',
        'CARRIER_ACCOUNT:READ',
        'CARRIER_ACCOUNT:UPDATE',
        'MAIL_ROUTING:READ',
        'EXPIRY_ALERT:READ',
        'ORDER:READ',
      ],
      sections: [
        {
          label: t('backoffice.sidebar_group_site_settings', { defaultValue: 'Site settings' }),
          children: [
            {
              label: t('backoffice.sidebar_display', { defaultValue: 'Display' }),
              path: wsPath('/workspace/display'),
              icon: <LayoutTemplate className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
            },
            {
              label: t('backoffice.sidebar_site_content', { defaultValue: 'Site content' }),
              path: wsPath('/workspace/site-content'),
              icon: <FileText className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
            },
            {
              label: t('backoffice.sidebar_hero'),
              path: wsPath('/workspace/hero'),
              icon: <Image className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['HERO:READ'],
            },
          ],
        },
        {
          label: t('backoffice.sidebar_group_system', { defaultValue: 'System settings' }),
          children: [
            {
              label: t('backoffice.sidebar_app_parameters', {
                defaultValue: 'App parameters',
              }),
              path: wsPath('/workspace/app-parameters'),
              icon: <SlidersHorizontal className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
            },
            {
              label: t('backoffice.sidebar_department_management', { defaultValue: 'Department management' }),
              path: '/workspace/department-management',
              icon: <Settings className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['DEPARTMENT:READ', 'DEPARTMENT:CREATE', 'DEPARTMENT:UPDATE'],
            },
            {
              label: t('backoffice.sidebar_currencies', 'Currencies'),
              path: wsPath('/workspace/currencies'),
              icon: <Coins className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['CURRENCY:READ'],
            },
            {
              label: t('backoffice.sidebar_delivery_pricing', { defaultValue: 'Delivery Settings' }),
              path: wsPath('/workspace/delivery-pricing'),
              icon: <Route className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['DELIVERY_PRICING:READ'],
            },
            {
              label: t('backoffice.sidebar_carrier_accounts', { defaultValue: 'Carrier accounts' }),
              path: wsPath('/workspace/carrier-accounts'),
              icon: <Truck className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['CARRIER_ACCOUNT:READ', 'CARRIER_ACCOUNT:UPDATE'],
            },
            {
              label: t('backoffice.sidebar_ship_from', { defaultValue: 'Ship-from location' }),
              path: wsPath('/workspace/ship-from'),
              icon: <Route className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['CARRIER_ACCOUNT:READ', 'CARRIER_ACCOUNT:UPDATE'],
            },
            {
              label: t('backoffice.sidebar_geo_access', { defaultValue: 'Country access control' }),
              path: wsPath('/workspace/geo-access'),
              icon: <Globe className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['UI_SETTING:READ', 'UI_SETTING:UPDATE'],
            },
            {
              label: t('backoffice.sidebar_mail_routing', { defaultValue: 'Email senders' }),
              path: wsPath('/workspace/mail-routing'),
              icon: <Send className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['MAIL_ROUTING:READ'],
            },
            {
              label: t('backoffice.sidebar_expiry_alerts', 'Cron jobs'),
              path: wsPath('/workspace/cron-jobs'),
              icon: <Mail className={iconClass} strokeWidth={2} />,
              requiredAnyOf: ['EXPIRY_ALERT:READ', 'DELIVERY_PRICING:READ', 'ORDER:READ'],
            },
          ],
        },
      ],
    },
  ];
}

